<template>
  <modal
    v-if="isVisible"
    elem-id="modal_lgpd_aceite_detalhe"
    modal-size="modal-md"
    :title="t('Aceite #{id}', { id: currentId })"
    :show="isVisible"
    :close-label="t('Fechar')"
    close-test-id="lgpd-aceite-detalhe-close-btn"
    @close="closeModal"
    @dismiss="closeModal"
    data-testid="lgpd-aceite-detalhe-modal"
  >
    <template #default>
      <div v-if="isLoading" class="loading-container">
        <spinner style="width: 32px; height: 32px;" />
      </div>
      <div v-else-if="detail" class="detail-content">
        <div class="detail-section">
          <h4 class="detail-section-title">{{ t('Conteúdo do termo no momento do aceite') }}</h4>
          <div class="snapshot-box" data-testid="lgpd-aceite-detalhe-snapshot">
            <pre class="snapshot-text">{{ detail.termo_conteudo }}</pre>
          </div>
        </div>

        <div class="detail-section">
          <h4 class="detail-section-title">{{ t('Informações do cliente') }}</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">{{ t('Cliente') }}</span>
              <span class="detail-value">{{ detail.cliente_nome }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ t('Termo') }}</span>
              <span class="detail-value">{{ detail.termo_versao }} - {{ detail.termo_titulo }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ t('Data/hora') }}</span>
              <span class="detail-value">{{ detail.created_at }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h4 class="detail-section-title">{{ t('Auditoria') }}</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">{{ t('IP') }}</span>
              <span class="detail-value mono">{{ detail.ip_address || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ t('User Agent') }}</span>
              <span class="detail-value mono" style="font-size: 11px;">{{ detail.user_agent || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ t('Registrado por') }}</span>
              <span class="detail-value">{{ detail.recorded_by || '-' }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #d1d5db;"></i>
        <p>{{ t('Registro não encontrado') }}</p>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useLgpdAdmin } from '../../composables/useLgpdAdmin';
import type { LgpdTermAcceptances_ListRow } from '../../contracts/LgpdTermAcceptances_ListRow';

const { t } = useI18n();
const { getAcceptanceDetail } = useLgpdAdmin();

const isVisible = ref(false);
const isLoading = ref(false);
const currentId = ref<number | null>(null);
const detail = ref<LgpdTermAcceptances_ListRow | null>(null);

const closeModal = (): void => {
  log('LgpdAceiteDetalheModal', 'closeModal', 'Closing');
  isVisible.value = false;
  detail.value = null;
  currentId.value = null;
};

const loadDetail = async (id: number): Promise<void> => {
  log('LgpdAceiteDetalheModal', 'loadDetail', 'Loading detail', id);
  isLoading.value = true;
  try {
    detail.value = await getAcceptanceDetail(id);
  } catch (err) {
    log('LgpdAceiteDetalheModal', 'loadDetail', 'Error', err);
  } finally {
    isLoading.value = false;
  }
};

const handleOpen = (data?: { id: number }): void => {
  log('LgpdAceiteDetalheModal', 'handleOpen', 'Opening', data);
  if (!data?.id) return;
  currentId.value = data.id;
  loadDetail(data.id);
  isVisible.value = true;
};

onMounted(() => {
  eventBus.on('modal.lgpd-aceite.open', handleOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.lgpd-aceite.open', handleOpen);
});
</script>

<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
  color: #6b7280;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
}

.snapshot-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
}

.snapshot-text {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #374151;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  color: #374151;
}

.detail-value.mono {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  word-break: break-all;
}
</style>
